import { Permissions } from '../../models/enums/permissions';
import { AnyReport } from '../../models/enums/permission-groups';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { HasPermission } from '../../containers/authorization-helpers/HasPermission';
import { HasServiceCenters } from '../../containers/authorization-helpers/HasServiceCenter';
import { useMediaQuery } from '../../hooks/use-media-query';
import { IApplicationState } from '../../store/IApplicationState';
import { Routes } from '../../utils/app-constants';
import {
    activeSectionTitle,
    FIXED_OPEN_SECTION_LIMIT,
    readCollapsed,
    visibleSectionCount,
    writeCollapsed
} from './navbar-state';
import './Navbar.scss';
import { NavbarGroup } from './NavbarGroup';

// #region Component Props

export interface INavbarGroupLinkProps {
    exact : boolean;
    title                   : string;
    path                    : string;
    permissions             : Permissions[];
    requiredServiceCenters  : string[];
}

export class NavbarGroupProps {
    public title                   : string;
    public icon                    : string;
    public links                   : INavbarGroupLinkProps[];
    public requiresServiceCenter   : boolean;
    public sectionPermissions      : Permissions[];
}

interface IProps {
    routes: NavbarGroupProps[];

    // A link in the sidebar was followed, which is the time for a sidebar that slides over the page to slide away
    onNavigate?: () => void;

    // The service center the person works in, at the top of the sidebar, and the person, at the bottom of it.  They are
    // what the sidebar is given, as it does not know how either is chosen or signed out
    serviceCenter?: React.ReactNode;
    account?: React.ReactNode;
}

// Where a section's links are shown, when the sidebar is collapsed to icons and the section is clicked
interface IFlyout {
    title   : string;
    top     : number;
    left    : number;
}

// #endregion

// With the sidebar collapsed to icons, a section's links open when the pointer rests on its icon, and go when it has
// been away from the icon and the links a moment, so that it can get from one to the other
const HOVER_OPEN_DELAY = 120;
const HOVER_CLOSE_DELAY = 250;

// The sidebar is only collapsed to icons on a screen wide enough for it to sit beside the page: the rest of the
// time it slides in over the page and is always open (see Responsive.scss)
const COLLAPSIBLE_WIDTH = '(min-width: 1001px)';

export const Navbar = (props: IProps) => {

    const location = useLocation();
    const isWide = useMediaQuery(COLLAPSIBLE_WIDTH);
    const navRef = React.useRef<HTMLElement>(null);
    const [isCollapsed, setIsCollapsed] = React.useState<boolean>(readCollapsed);
    const [openTitle, setOpenTitle] = React.useState<string | undefined>(
        () => activeSectionTitle(props.routes, location.pathname)
    );
    const [flyout, setFlyout] = React.useState<IFlyout | undefined>(undefined);
    const hoverTimer = React.useRef<number | undefined>(undefined);

    const permissions = useSelector((state: IApplicationState) => state.userdata.user.permissions);
    const serviceCenters = useSelector((state: IApplicationState) => state.userdata.user.serviceCenters);

    const isRail = isCollapsed && isWide;
    const currentTitle = activeSectionTitle(props.routes, location.pathname);

    // With only a few sections every one of them is open, and none can be closed.  The rail has no sections to open
    const isFixedOpen = !isRail && visibleSectionCount(props.routes, permissions, serviceCenters) <= FIXED_OPEN_SECTION_LIMIT;

    // The section of the page opens, as the page changes
    React.useEffect(() => {
        if (currentTitle) {
            setOpenTitle(currentTitle);
        }
        setFlyout(undefined);
    }, [location.pathname]);

    // The header and the footer follow the sidebar in and out
    React.useEffect(() => {
        document.body.classList.toggle('sidebar-collapsed', isRail);
        return () => document.body.classList.remove('sidebar-collapsed');
    }, [isRail]);

    // A flyout goes away when anything else is clicked, or Escape is pressed
    React.useEffect(() => {

        if (!flyout) {
            return;
        }

        const onMouseDown = (e : MouseEvent) => {
            const flyoutGroup = navRef.current ? navRef.current.querySelector('.app-module.flyout') : null;
            if (!flyoutGroup || !flyoutGroup.contains(e.target as Node)) {
                setFlyout(undefined);
            }
        };

        const onKeyDown = (e : KeyboardEvent) => {
            if (e.key === 'Escape') {
                setFlyout(undefined);
            }
        };

        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };

    }, [flyout]);

    const clearHoverTimer = () => {
        if (hoverTimer.current !== undefined) {
            window.clearTimeout(hoverTimer.current);
            hoverTimer.current = undefined;
        }
    };

    React.useEffect(() => clearHoverTimer, []);

    const onToggle = () => {
        clearHoverTimer();
        const collapsed = !isCollapsed;
        setIsCollapsed(collapsed);
        writeCollapsed(collapsed);
        setFlyout(undefined);
    };

    const showFlyout = (title : string, header : HTMLElement) => {
        const nav = navRef.current ? navRef.current.getBoundingClientRect() : undefined;
        setFlyout({
            title,
            top: header.getBoundingClientRect().top,
            left: nav ? nav.right : 0
        });
    };

    // Open the section in the sidebar, and close the one that was open, or with the sidebar collapsed to icons
    // show its links next to the icon.  Sections that are all kept open do nothing
    const onHeaderClick = (title : string, header : HTMLElement) => {

        if (isRail) {
            // Clicking opens the links, for a screen with no pointer to rest on the icon.  It does not close them: with
            // a pointer they have already opened by the time it is clicked
            clearHoverTimer();
            if (!flyout || flyout.title !== title) {
                showFlyout(title, header);
            }
            return;
        }

        if (!isFixedOpen) {
            setOpenTitle(openTitle === title ? undefined : title);
        }
    };

    const onHoverStart = (title : string, header : HTMLElement) => {

        if (!isRail) {
            return;
        }

        clearHoverTimer();
        if (flyout && flyout.title === title) {
            return;
        }

        hoverTimer.current = window.setTimeout(() => {
            hoverTimer.current = undefined;
            showFlyout(title, header);
        }, HOVER_OPEN_DELAY);
    };

    const onHoverEnd = () => {

        if (!isRail) {
            return;
        }

        clearHoverTimer();
        if (flyout) {
            hoverTimer.current = window.setTimeout(() => {
                hoverTimer.current = undefined;
                setFlyout(undefined);
            }, HOVER_CLOSE_DELAY);
        }
    };

    const onLinkClick = (e : React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (props.onNavigate && target.closest && target.closest('a')) {
            props.onNavigate();
        }
    };

    const getNavbarGroup = (item: NavbarGroupProps) => (
        <NavbarGroup
            {...item }
            isOpen={ !isRail && (isFixedOpen || openTitle === item.title) }
            isFixed={ isFixedOpen }
            isCurrent={ currentTitle === item.title }
            isFlyout={ flyout !== undefined && flyout.title === item.title }
            flyoutTop={ flyout ? flyout.top : undefined }
            flyoutLeft={ flyout ? flyout.left : undefined }
            onHeaderClick={ onHeaderClick }
            onHoverStart={ onHoverStart }
            onHoverEnd={ onHoverEnd } />
    );

    return <nav
        ref={ navRef }
        className={ isRail ? 'col-md-2 d-none d-md-block sidebar collapsed' : 'col-md-2 d-none d-md-block sidebar' }
        onClick={ onLinkClick }>
        <div className="sidebar-sticky">
            { props.serviceCenter }
            { props.serviceCenter ? <hr className="sidebar-separator" /> : null }
            <HasPermission requiredPermissions={ [Permissions.Dashboard] }>
                <NavLink to={ Routes.DASHBOARD } exact className="dashboard-module" title="Dashboard">
                    <i className="la la-th-large" />
                    <span className="module-title">Dashboard</span>
                </NavLink>
            </HasPermission>
            {
                props.routes.map((item: NavbarGroupProps, idx: number) => (
                    <HasPermission
                        key={idx}
                        requiredPermissions={ item.sectionPermissions }>
                        {
                            item.requiresServiceCenter ?
                                <HasServiceCenters>
                                    {
                                        getNavbarGroup(item)
                                    }
                                </HasServiceCenters> :
                                getNavbarGroup(item)
                        }
                    </HasPermission>
                ))
            }
            <HasPermission
                key={''}
                requiredPermissions={[
                    ...AnyReport]
                }>
                <hr className="sidebar-separator" />
                <NavLink to={ Routes.REPORTS_REPORTS } className="dashboard-module" title="Reports">
                    <i className="la la-bar-chart" />
                    <span className="module-title">Reports</span>
                </NavLink>
            </HasPermission>
        </div>
        { props.account }
        <button
            type="button"
            className="sidebar-toggle"
            aria-label={ isCollapsed ? 'Expand menu' : 'Collapse menu' }
            aria-expanded={ !isCollapsed }
            title={ isCollapsed ? 'Expand menu' : 'Collapse menu' }
            onClick={ onToggle }>
            <i className="la la-angle-double-left" />
        </button>
    </nav>;
};
